import { Component, computed, inject, Input, OnInit, signal } from "@angular/core";

import { FormsModule } from "@angular/forms";
import { OperationRequirement, OperationRequirementService } from "../../../_services/operation-requirement.service";
import { ArticleService } from "../../../_services";
import { ArticleTemplate } from "../../../_models";
import { ShippingConfig } from "../../../_models/shipping-config.interface";
import { StagingConfigService } from "../../../_services/staging-config.service";
import { firstValueFrom } from "rxjs";
import { OperationShippingConfigComponent } from "../operation-shipping-config/operation-shipping-config.component";
import { OperationArchivedShippingConfigsComponent } from "../operation-archived-shipping-configs/operation-archived-shipping-configs.component";

type ActionType = "STAGING" | "VERSAND";

@Component({
  selector: "app-operation-requirements",
  standalone: true,
  imports: [FormsModule, OperationShippingConfigComponent, OperationArchivedShippingConfigsComponent],
  templateUrl: "./operation-requirements.component.html",
})
export class OperationRequirementsComponent implements OnInit {
  @Input() operationId!: number;
  @Input() projectId?: number;
  // Inputs fuer die eingebetteten Versandkonfig-Forms
  @Input() ticketStoreId?: number;
  @Input() ticketLat?: number;
  @Input() ticketLng?: number;
  @Input() referenceDate?: string;
  @Input() operationTechnicianId?: number;

  private service = inject(OperationRequirementService);
  private articleService = inject(ArticleService);
  private stagingConfigService = inject(StagingConfigService);

  protected requirements = signal<OperationRequirement[]>([]);
  protected showModal = signal<boolean>(false);
  protected editingRequirement = signal<OperationRequirement | null>(null);
  protected searchResults = signal<ArticleTemplate[]>([]);
  protected selectedTemplate = signal<ArticleTemplate | null>(null);

  // Gefilterte Sichten — vom Template direkt verwendet
  protected versandRequirements = computed(() =>
    this.requirements().filter((r) => r.action_type === "VERSAND"),
  );
  protected stagingRequirements = computed(() =>
    this.requirements().filter((r) => r.action_type === "STAGING"),
  );

  // Aktive ShippingConfig (vom hawk-operation-shipping-config-Sub-Component
  // per (configChanged) gespeist). Existierende archivierte Configs werden
  // separat angezeigt (siehe Template).
  protected shippingConfig = signal<ShippingConfig | null>(null);
  // Marker, der bei jedem configChanged hochzaehlt — triggert das Reload der
  // archivierten Configs (z.B. nach Versand-Abschluss in der LagerApp).
  protected archivedRefreshKey = signal<number>(0);

  // Marker fuer das Reload der aktiven ShippingConfig — wird hochgezaehlt
  // nach Versand-Req-Aenderungen, weil das Backend dabei automatisch eine
  // aktive Config anlegt, falls noch keine existiert.
  protected activeConfigRefreshKey = signal<number>(0);

  protected onActiveConfigChanged(config: ShippingConfig | null): void {
    this.shippingConfig.set(config);
    this.archivedRefreshKey.update((k) => k + 1);
  }

  // Staging-Artikelvorlagen (nur die aus Staging-Configs des Projekts)
  protected stagingTemplates = signal<{ id: number; description: string }[]>([]);
  protected allTemplates = signal<ArticleTemplate[]>([]);

  // Formular-Felder. formActionType wird durch den Klick auf den jeweiligen
  // Section-Add-Button gesetzt — kein Type-Switch im Modal mehr.
  protected formActionType: ActionType = "VERSAND";
  protected formQuantity = 1;
  protected formSearchText = "";
  protected formStagingTemplateId: number | null = null;

  private searchDebounce: ReturnType<typeof setTimeout> | null = null;

  async ngOnInit() {
    this.requirements.set(await this.service.loadRequirements(this.operationId));
    await this.loadTemplates();
  }

  private async loadTemplates() {
    // Alle Artikelvorlagen laden (für Versand-Suche)
    const all = await firstValueFrom(this.articleService.loadArticleTemplates());
    this.allTemplates.set(all || []);

    // Staging-Artikelvorlagen aus den Staging-Configs des Projekts laden
    if (this.projectId) {
      await this.stagingConfigService.loadConfigs(this.projectId);
      const configs = this.stagingConfigService.configs();
      const templates = configs
        .filter((c) => c.enabled && c.article_template_id && c.article_template_description)
        .map((c) => ({ id: c.article_template_id!, description: c.article_template_description! }));
      // Duplikate entfernen
      const unique = [...new Map(templates.map((t) => [t.id, t])).values()];
      this.stagingTemplates.set(unique);
    }
  }

  // --- Modal öffnen/schließen ---

  openAdd(actionType: ActionType) {
    this.resetForm();
    this.formActionType = actionType;
    this.editingRequirement.set(null);
    this.showModal.set(true);
  }

  openEdit(req: OperationRequirement) {
    this.resetForm();
    this.editingRequirement.set(req);
    this.formActionType = req.action_type;
    this.formQuantity = req.quantity;

    if (req.action_type === "STAGING" && req.article_template_id) {
      this.formStagingTemplateId = req.article_template_id;
      this.selectedTemplate.set({ id: req.article_template_id, description: req.article_template_description || "" } as ArticleTemplate);
    } else if (req.article_template_id) {
      this.selectedTemplate.set({ id: req.article_template_id, description: req.article_template_description || "" } as ArticleTemplate);
      this.formSearchText = req.article_template_description || "";
    } else {
      this.formSearchText = req.free_text || "";
    }

    this.showModal.set(true);
  }

  closeModal() {
    this.showModal.set(false);
    this.editingRequirement.set(null);
    this.resetForm();
  }

  onBackdropClick(event: MouseEvent) {
    // Nur schließen wenn direkt auf den Backdrop geklickt wird
    if (event.target === event.currentTarget) {
      this.closeModal();
    }
  }

  // --- Formular-Logik ---

  onStagingTemplateSelect(templateId: number | null) {
    if (!templateId) {
      this.selectedTemplate.set(null);
      return;
    }
    const tpl = this.stagingTemplates().find((t) => t.id === templateId);
    if (tpl) {
      this.selectedTemplate.set({ id: tpl.id, description: tpl.description } as ArticleTemplate);
    }
  }

  onSearchInput() {
    this.selectedTemplate.set(null);
    if (this.searchDebounce) clearTimeout(this.searchDebounce);

    if (this.formSearchText.trim().length < 2) {
      this.searchResults.set([]);
      return;
    }

    this.searchDebounce = setTimeout(() => this.performSearch(), 300);
  }

  private performSearch() {
    const query = this.formSearchText.toLowerCase();
    const results = this.allTemplates().filter(
      (t) => t.description?.toLowerCase().includes(query) || t.part_nr?.toLowerCase().includes(query),
    );
    this.searchResults.set(results.slice(0, 15));
  }

  selectTemplate(template: ArticleTemplate) {
    this.selectedTemplate.set(template);
    this.formSearchText = template.description;
    this.searchResults.set([]);
  }

  onSearchBlur() {
    this.searchResults.set([]);
  }

  protected formMinQuantity = computed<number>(() => {
    const editing = this.editingRequirement();
    if (!editing || editing.action_type !== "VERSAND") return 1;
    return Math.max(1, editing.already_shipped_quantity || 0);
  });

  canSave(): boolean {
    if (this.formQuantity < this.formMinQuantity()) return false;
    if (this.formActionType === "STAGING") {
      return this.selectedTemplate() !== null;
    }
    return this.selectedTemplate() !== null || this.formSearchText.trim().length > 0;
  }

  async saveRequirement() {
    const template = this.selectedTemplate();
    const data: Partial<OperationRequirement> = {
      action_type: this.formActionType,
      quantity: this.formQuantity,
    };

    if (template) {
      data.article_template_id = template.id;
      data.free_text = null as any;
    } else {
      data.free_text = this.formSearchText.trim();
      data.article_template_id = null as any;
    }

    const editing = this.editingRequirement();
    if (editing) {
      // Bestehendes Requirement aktualisieren
      const updated = await this.service.updateRequirement(editing.id, data);
      if (updated) {
        this.requirements.update((reqs) => reqs.map((r) => (r.id === editing.id ? updated : r)));
        this.closeModal();
        // Backend legt bei Versand-Mengen-Erhoehung evtl. eine Auto-Config an
        if (updated.action_type === 'VERSAND') this.activeConfigRefreshKey.update((k) => k + 1);
      }
    } else {
      // Neues Requirement anlegen
      const result = await this.service.createRequirement(this.operationId, data);
      if (result) {
        this.requirements.update((reqs) => [...reqs, result]);
        this.closeModal();
        if (result.action_type === 'VERSAND') this.activeConfigRefreshKey.update((k) => k + 1);
      }
    }
  }

  async deleteRequirement(req: OperationRequirement) {
    const label = req.article_template_description || req.free_text || 'Unbekannt';
    if (!confirm(`"${req.quantity}x ${label}" wirklich löschen?`)) return;
    const success = await this.service.deleteRequirement(req.id);
    if (success) {
      this.requirements.update((reqs) => reqs.filter((r) => r.id !== req.id));
    }
  }

  // Legt Versand-Requirements aus dem Legacy-Description-Parsing an. Nur
  // erlaubt, solange noch keine Versand-Requirements existieren — sonst
  // kaeme es zu Doppelungen mit den Buero-Eintraegen.
  protected parsingInProgress = signal<boolean>(false);

  async parseFromDescription() {
    if (this.versandRequirements().length > 0) return;
    if (!confirm('Versand-Artikel aus der Auftragsbeschreibung erzeugen?')) return;

    this.parsingInProgress.set(true);
    try {
      const result = await this.service.parseFromDescription(this.operationId);
      if ('error' in result) {
        alert(result.error);
        return;
      }
      this.requirements.update((reqs) => [...reqs, ...result.requirements]);
      // Parsing legt im Backend automatisch eine Versandconfig an (sofern noch
      // keine existiert) — Reload triggern, damit sie ohne Page-Reload erscheint.
      this.activeConfigRefreshKey.update((k) => k + 1);
    } finally {
      this.parsingInProgress.set(false);
    }
  }

  private resetForm() {
    this.formActionType = "VERSAND";
    this.formQuantity = 1;
    this.formSearchText = "";
    this.selectedTemplate.set(null);
    this.formStagingTemplateId = null;
    this.searchResults.set([]);
  }
}
